import React, { useEffect, useMemo, useState } from "react";
import "../../VideoManager.css";

const COLOR_GRADES = {
  cinematic: "brightness(110%) contrast(120%) saturate(130%)",
  vibrant: "brightness(115%) contrast(110%) saturate(150%)",
  monochrome: "grayscale(70%) contrast(125%)",
};

const LAYOUT_PRESETS = [
  { id: "single", label: "Single focus" },
  { id: "side", label: "Side-by-side" },
  { id: "grid", label: "Grid tiles" },
];

export default function AdvancedVideoEditor() {
  const [clipQueue, setClipQueue] = useState([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(30);
  const [layout, setLayout] = useState(LAYOUT_PRESETS[0].id);
  const [backgroundAudio, setBackgroundAudio] = useState(null);
  const [backgroundAudioUrl, setBackgroundAudioUrl] = useState("");
  const [backgroundLevel, setBackgroundLevel] = useState(60);
  const [colorGrade, setColorGrade] = useState("cinematic");
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [audioReplacementEnabled, setAudioReplacementEnabled] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    return () => {
      clipQueue.forEach((clip) => URL.revokeObjectURL(clip.url));
      if (backgroundAudioUrl) URL.revokeObjectURL(backgroundAudioUrl);
    };
  }, [clipQueue, backgroundAudioUrl]);

  const handleClipUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const newClip = { id: Date.now(), name: file.name, file, url };
    setClipQueue((prev) => [...prev, newClip].slice(-4));
    setPreviewUrl(url);
  };

  const handleBackgroundAudio = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (backgroundAudioUrl) {
      URL.revokeObjectURL(backgroundAudioUrl);
    }
    const url = URL.createObjectURL(file);
    setBackgroundAudio(file);
    setBackgroundAudioUrl(url);
  };

  const handleRemoveClip = (clipId) => {
    setClipQueue((prev) => prev.filter((clip) => clip.id !== clipId));
    if (clipQueue[0]?.id === clipId) {
      const nextClip = clipQueue[1];
      setPreviewUrl(nextClip?.url || "");
    }
  };

  const combinedPreview = previewUrl || clipQueue[0]?.url || "";
  const downloadName =
    clipQueue.length > 0
      ? `${clipQueue[0].name.replace(/\.[^.]+$/, "")}-advanced.mp4`
      : "gcc-advanced-edit.mp4";

  const trimSummary = useMemo(() => {
    if (!combinedPreview) return "Trim range unavailable until you load a clip.";
    return `${trimStart.toFixed(1)}s → ${trimEnd.toFixed(1)}s`;
  }, [trimStart, trimEnd, combinedPreview]);

  const filterCss = useMemo(() => {
    const gradeFilter = COLOR_GRADES[colorGrade] || "";
    return `${gradeFilter} brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
  }, [colorGrade, brightness, contrast, saturation]);

  const backgroundAudioInfo = backgroundAudio ? `${backgroundAudio.name} (${(backgroundAudio.size / 1e6).toFixed(2)} MB)` : "None";

  return (
    <section className="studio-panel">
      <div className="studio-panel__header">
        <h2>Advanced Video Editor</h2>
        <p>Trim, stack, and color-grade clips, mix audio, or tile multiple angles into a cinematic worship story.</p>
      </div>

      <div className="studio-panel__dual-grid video-studio__layout">
        <div className="studio-panel__controls-column">
          <div className="studio-panel__section-card studio-panel__compact-section">
            <h4>Timeline & trimming</h4>
            <input type="file" accept="video/*" onChange={handleClipUpload} />
            <p className="studio-panel__helper">{clipQueue.length > 0 ? `${clipQueue.length} clip(s) queued` : "Queue up to four clips."}</p>
            <div className="studio-panel__options">
              <label>
                <span>Trim start (s)</span>
                <input
                  type="number"
                  min={0}
                  value={trimStart}
                  onChange={(event) => setTrimStart(Number(event.target.value))}
                />
              </label>
              <label>
                <span>Trim end (s)</span>
                <input
                  type="number"
                  min={trimStart + 0.5}
                  value={trimEnd}
                  onChange={(event) => setTrimEnd(Number(event.target.value))}
                />
              </label>
            </div>
            <div className="studio-panel__section-card studio-panel__compact-section" style={{ marginTop: "0.5rem" }}>
              <span className="studio-panel__helper">Slide to cut the source clip</span>
              <input
                type="range"
                min={0}
                max={trimEnd || trimStart + 30}
                value={trimStart}
                onChange={(event) => setTrimStart(Number(event.target.value))}
              />
              <input
                type="range"
                min={trimStart + 0.5}
                max={Math.max(trimStart + 0.5, trimEnd)}
                value={trimEnd}
                onChange={(event) => setTrimEnd(Number(event.target.value))}
              />
            </div>
            <p className="studio-panel__helper">Trim range: {trimSummary}</p>
            <div className="studio-panel__section-grid">
              {clipQueue.map((clip) => (
                <div key={clip.id} className="studio-panel__section-card">
                  <strong>{clip.name}</strong>
                  <span>Queued for tile layout.</span>
                  <div className="studio-panel__preview-actions">
                    <button
                      type="button"
                      className="studio-panel__cta studio-panel__cta--secondary"
                      onClick={() => handleRemoveClip(clip.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="studio-panel__section-card studio-panel__compact-section">
            <h4>Layout & aesthetics</h4>
            <div className="studio-panel__options">
              {LAYOUT_PRESETS.map((preset) => (
                <label key={preset.id} className="studio-panel__checkbox-label">
                  <input
                    type="radio"
                    name="layout"
                    value={preset.id}
                    checked={layout === preset.id}
                    onChange={(event) => setLayout(event.target.value)}
                  />
                  <span>{preset.label}</span>
                </label>
              ))}
            </div>
            <div className="studio-panel__row-grid">
              <label>
                <span>Color grade</span>
                <select value={colorGrade} onChange={(event) => setColorGrade(event.target.value)}>
                  <option value="cinematic">Cinematic boost</option>
                  <option value="vibrant">Vibrant worship</option>
                  <option value="monochrome">Monochrome reflect</option>
                </select>
              </label>
              <label>
                <span>Brightness</span>
                <input
                  type="range"
                  min={80}
                  max={140}
                  value={brightness}
                  onChange={(event) => setBrightness(Number(event.target.value))}
                />
              </label>
              <label>
                <span>Contrast</span>
                <input
                  type="range"
                  min={80}
                  max={140}
                  value={contrast}
                  onChange={(event) => setContrast(Number(event.target.value))}
                />
              </label>
              <label>
                <span>Saturation</span>
                <input
                  type="range"
                  min={70}
                  max={160}
                  value={saturation}
                  onChange={(event) => setSaturation(Number(event.target.value))}
                />
              </label>
            </div>
            <p className="studio-panel__helper">{`Depth: ${layout} · Grade: ${colorGrade}`}</p>
          </div>

          <div className="studio-panel__section-card studio-panel__compact-section">
            <h4>Audio studio</h4>
            <div className="studio-panel__row-grid">
              <label>
                <span>Background audio</span>
                <input type="file" accept="audio/*" onChange={handleBackgroundAudio} />
                <p className="studio-panel__helper">{backgroundAudioInfo}</p>
              </label>
              <label>
                <span>Mix level {backgroundLevel}%</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={backgroundLevel}
                  onChange={(event) => setBackgroundLevel(Number(event.target.value))}
                />
              </label>
            </div>
            <label className="studio-panel__checkbox-label">
              <input
                type="checkbox"
                checked={audioReplacementEnabled}
                onChange={(event) => setAudioReplacementEnabled(event.target.checked)}
              />
              <span>Replace original audio</span>
            </label>
          </div>
        </div>

        <div className="studio-panel__preview-column">
          <div className="recording-studio__preview-card">
            <div className="recording-studio__label">Preview</div>
            {combinedPreview ? (
              <video
                src={combinedPreview}
                controls
                className="studio-panel__video-preview"
                style={{ filter: filterCss }}
                playsInline
              />
            ) : (
              <div className="recording-studio__placeholder">
                Load a clip to preview the timeline, layout, and mix choices.
              </div>
            )}
            <div className="studio-panel__preview-actions">
              {combinedPreview && (
                <a className="studio-panel__cta studio-panel__cta--secondary" href={combinedPreview} download={downloadName}>
                  Download edit
                </a>
              )}
              <button type="button" className="studio-panel__cta">
                Export for review
              </button>
            </div>
          </div>

          <div className="studio-panel__section-card">
            <h4>Background audio</h4>
            {backgroundAudioUrl ? (
              <audio controls src={backgroundAudioUrl} className="studio-panel__audio-preview" />
            ) : (
              <p className="studio-panel__helper">Add ambient music or worship stems.</p>
            )}
            <div className="studio-panel__preview-actions">
              <button type="button" className="studio-panel__cta studio-panel__cta--secondary" disabled={!backgroundAudio}>
                Download track
              </button>
            </div>
          </div>

          <div className="studio-panel__section-card">
            <h4>Notes & export</h4>
            <textarea
              className="studio-panel__form-textarea"
              placeholder="Document trim cues, layout choices, or mix instructions..."
              rows={4}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
            <div className="studio-panel__preview-actions">
              <button type="button" className="studio-panel__cta studio-panel__cta--secondary">
                Save notes
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
